/** Controlled store authorization wire contract, shared with present_workspace_panel. */
export interface StoreAuthAction {
  id: string;
  platform?: General.Platform;
  storeType?: IM_API.Admin.StoreType;
  region?: General.UpperCaseRegion;
  shopeeShopType?: 'CrossOfficial' | 'Cross3PF' | 'Local';
  departmentIds?: number[];
}
const localRegions: Record<string, string[]> = {
  SHOPEE: 'PH TH ID MY VN SG BR MX CO PL ES CL FR AR'.split(' '),
  LAZADA: 'SG TH MY PH VN ID'.split(' '),
  MERCADO: 'BR MX AR CO CL'.split(' '),
  KWAI: ['BR'],
  TIKTOK: 'VN TH MY SG PH GB ES IT DE FR JP MX PL NL BE CZ AT GR PT HU ID IE BR'.split(' '),
  TIKTOK_US: [],
  FACEBOOK: [],
};
const tiktokCrossRegions = localRegions.TIKTOK.filter(
  (region) => !['ID', 'IE', 'BR'].includes(region),
);
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export function parseStoreAuth(raw: unknown): StoreAuthAction | undefined {
  try {
    const root = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (
      !record(root) ||
      root.version !== 1 ||
      !Array.isArray(root.widgets) ||
      root.widgets.length !== 1
    )
      return;
    const widget = root.widgets[0];
    if (
      !record(widget) ||
      widget.type !== 'workspace_panel' ||
      widget.panel !== 'store_auth' ||
      Object.keys(widget).some((key) => !['type', 'id', 'panel', 'params'].includes(key)) ||
      typeof widget.id !== 'string' ||
      !widget.id.trim() ||
      widget.id.length > 64
    )
      return;
    const params = parseStoreAuthParams(widget.params === undefined ? {} : widget.params);
    return params ? { id: widget.id.trim(), ...params } : undefined;
  } catch {
    return;
  }
}

export function parseStoreAuthParams(params: unknown): Omit<StoreAuthAction, 'id'> | undefined {
  if (
    !record(params) ||
    Object.keys(params).some(
      (key) => !['external_type', 'store_type', 'region', 'department_ids'].includes(key),
    )
  )
    return;
  if (params.external_type === undefined) {
    for (const external_type of Object.keys(localRegions)) {
      const parsed = parseStoreAuthParams({ ...params, external_type });
      if (parsed) return { ...parsed, platform: undefined };
    }
    return;
  }
  const platform = params.external_type;
  if (typeof platform !== 'string' || !Object.prototype.hasOwnProperty.call(localRegions, platform))
    return;
  const type = params.store_type;
  if (
    type !== undefined &&
    (typeof type !== 'string' ||
      !(
        platform === 'SHOPEE' ? ['CROSS_OFFICIAL', 'CROSS_3PF', 'LOCAL'] : ['CROSS', 'LOCAL']
      ).includes(type))
  )
    return;
  const region = params.region;
  if (region !== undefined) {
    const regions =
      platform === 'TIKTOK' && type === 'CROSS' ? tiktokCrossRegions : localRegions[platform];
    if (
      typeof region !== 'string' ||
      !regions.includes(region) ||
      (type !== undefined && type !== 'LOCAL' && platform !== 'TIKTOK')
    )
      return;
  }
  const departments = params.department_ids;
  if (
    departments !== undefined &&
    (!Array.isArray(departments) || departments.some((id) => !Number.isSafeInteger(id) || id <= 0))
  )
    return;
  return {
    platform: platform.toLowerCase() as General.Platform,
    storeType: type === undefined ? undefined : type === 'LOCAL' ? 'LOCAL' : 'CROSS',
    region: region as General.UpperCaseRegion | undefined,
    shopeeShopType:
      platform !== 'SHOPEE' || type === undefined
        ? undefined
        : type === 'LOCAL'
        ? 'Local'
        : type === 'CROSS_OFFICIAL'
        ? 'CrossOfficial'
        : 'Cross3PF',
    departmentIds: departments as number[] | undefined,
  };
}

export const STORE_AUTH_PLATFORMS = [
  ['SHOPEE', 'Shopee'],
  ['LAZADA', 'Lazada'],
  ['TIKTOK', 'TikTok'],
  ['MERCADO', 'Mercado'],
  ['KWAI', 'Kwai'],
  ['TIKTOK_US', 'TikTok US'],
  ['FACEBOOK', 'Facebook'],
] as const;

/** Preserve the original prefill and validate it against the selected platform. */
export function withStoreAuthPlatform(raw: unknown, platform: string): unknown {
  if (!parseStoreAuth(raw)) return;
  const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
  const widget = (value as { widgets: { params?: Record<string, unknown> }[] }).widgets[0];
  const payload = {
    version: 1,
    widgets: [{ ...widget, params: { ...widget.params, external_type: platform } }],
  };
  return parseStoreAuth(payload) ? payload : undefined;
}
